import Tooltip from '@components/tooltip';
import Typography from '@components/typography';
import React from 'react';
import '../../../styles/configure/pricingbreakdown.scss';
import { v4 as uuid4 } from 'uuid';
import { PricingBreakdownProps, PricingBreakdownItem } from 'interface';
import { PricingType } from '../../../enums';
import { allPriceExc, allPriceInc } from '../../../constants';

/**
 * The pricing breakdown component
 *
 * @param dataCy Cypress test name
 * @param items pricing breakdown items
 * @returns The pricing break component
 */
const PricingBreakdown: React.FC<PricingBreakdownProps> = ({
  'data-cy': dataCy,
  items,
  pricingType = PricingType.Personal,
}): JSX.Element => {
  const getItem = (item: PricingBreakdownItem) => {
    return (
      <li
        key={uuid4()}
        className="pricingbreakdown__list__item"
        data-cy={`${dataCy}-list-item`}
      >
        <Typography
          variant="body1"
          size="rg"
          weight="semi-bold"
          className="pricingbreakdown__list__item--label"
        >
          {item.label}:
        </Typography>
        {item.displayAsTooltip && (
          <Tooltip
            id={uuid4()}
            text={''}
            delayHide={100}
            data-cy="pricingbreakdown-tooltip"
          >
            <Typography variant="caption" size="xs" colour="alternative">
              {item.tooltip}
            </Typography>
          </Tooltip>
        )}
        <Typography
          variant="body1"
          size="sm"
          className="pricingbreakdown__list__item--value"
        >
          {item.value}
        </Typography>
        {!item.displayAsTooltip && (
          <Typography
            variant="body1"
            size="sm"
            className="pricingbreakdown__list__item--text"
          >
            {item.tooltip.split('{roadFundLicence}')[0]}
            <a
              href={item.tooltipHyperlink}
              data-testid="pricingbreakdown_road-fund-licence--hyperlink"
              target="_blank"
              rel="noopener noreferrer"
            >
              road fund licence
            </a>
            {item.tooltip.split('{roadFundLicence}')[1]}
          </Typography>
        )}
      </li>
    );
  };

  return (
    <div className="pricingbreakdown" data-cy={`${dataCy}-container`}>
      <Typography
        variant="h3"
        size="rg"
        className="pricingbreakdown__title"
        weight="bold"
      >
        Pricing breakdown
      </Typography>
      <Typography
        variant="body1"
        size="sm"
        data-testid="pricingbreakdown-all-price"
        data-cy="pricingbreakdown-all-price"
        className="pricingbreakdown__subtitle"
      >
        {pricingType === PricingType.Personal ? allPriceInc : allPriceExc}
      </Typography>
      <ul className="pricingbreakdown__list" data-cy={`${dataCy}-list`}>
        {items.map((m) => getItem(m))}
      </ul>
    </div>
  );
};

export default PricingBreakdown;
